import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import useBreakpoint from '../hooks/useBreakpoint';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
};

function SectionCard({ title, children }) {
  return (
    <div style={{ background: 'white', border: `1px solid ${T.border}`, borderRadius: 14, padding: '24px 28px', marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: T.muted, marginBottom: 20 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', disabled }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: T.muted, marginBottom: 6 }}>
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        style={{
          width: '100%', boxSizing: 'border-box',
          border: `1px solid ${T.border}`, borderRadius: 8,
          padding: '10px 12px', fontSize: 13, color: T.ink,
          background: disabled ? T.bg : 'white', outline: 'none',
          transition: 'border-color 150ms',
        }}
        onFocus={e => { if (!disabled) e.target.style.borderColor = T.ink; }}
        onBlur={e => e.target.style.borderColor = T.border}
      />
    </div>
  );
}

export default function Profile() {
  const { user, profile, refreshProfile, signOut } = useAuth();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const fileRef = useRef(null);

  const [form, setForm]       = useState({ first_name: '', last_name: '', company_name: '', phone: '', address: '', vat_id: '' });
  const [logoUrl, setLogoUrl] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [uploading, setUploading] = useState(false);

  const [pwForm, setPwForm]     = useState({ next: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const [deleteConfirm, setDeleteConfirm] = useState('');

  useEffect(() => {
    if (profile) {
      setForm({
        first_name:   profile.first_name   || '',
        last_name:    profile.last_name    || '',
        company_name: profile.company_name || '',
        phone:        profile.phone        || '',
        address:      profile.address      || '',
        vat_id:       profile.vat_id       || '',
      });
      setLogoUrl(profile.logo_url || null);
    }
  }, [profile]);

  function set(key) {
    return val => setForm(f => ({ ...f, [key]: val }));
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { addToast('Passwörter stimmen nicht überein.', 'error'); return; }
    if (pwForm.next.length < 8) { addToast('Passwort muss mindestens 8 Zeichen haben.', 'error'); return; }
    setPwSaving(true);
    const { error: pwErr } = await supabase.auth.updateUser({ password: pwForm.next });
    setPwSaving(false);
    if (pwErr) { addToast('Fehler: ' + pwErr.message, 'error'); }
    else { setPwForm({ next: '', confirm: '' }); addToast('Passwort geändert.', 'success'); }
  }

  async function handleDeleteAccount() {
    if (deleteConfirm !== 'LÖSCHEN') { addToast('Bitte gib LÖSCHEN zur Bestätigung ein.', 'error'); return; }
    if (!window.confirm('Konto und alle Daten unwiderruflich löschen?')) return;
    await supabase.from('profiles').update({ deleted_at: new Date().toISOString() }).eq('id', user.id);
    await signOut();
    navigate('/');
    addToast('Konto zur Löschung markiert. Wende dich an support@immohq.eu.', 'info', 8000);
  }

  async function handleLogoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext  = file.name.split('.').pop();
    const path = `${user.id}/logo.${ext}`;
    const { error: upErr } = await supabase.storage.from('logos').upload(path, file, { upsert: true });
    if (upErr) { addToast('Logo konnte nicht hochgeladen werden.', 'error'); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(path);
    await supabase.from('profiles').update({ logo_url: publicUrl }).eq('id', user.id);
    setLogoUrl(publicUrl);
    await refreshProfile();
    setUploading(false);
    addToast('Logo gespeichert.', 'success');
  }

  async function handleSave() {
    setSaving(true);
    const { error: saveErr } = await supabase
      .from('profiles')
      .update({ first_name: form.first_name, last_name: form.last_name, company_name: form.company_name, phone: form.phone, address: form.address, vat_id: form.vat_id })
      .eq('id', user.id);
    if (saveErr) { addToast('Speichern fehlgeschlagen: ' + saveErr.message, 'error'); }
    else { await refreshProfile(); addToast('Profil gespeichert.', 'success'); }
    setSaving(false);
  }

  return (
    <div style={{ maxWidth: 640, color: T.ink }}>

      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ margin: 0, fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-0.6px', color: T.ink }}>
          Profil
        </h1>
        <p style={{ margin: '5px 0 0', fontSize: 14, color: T.muted }}>
          Persönliche Daten, Firmendaten und Sicherheitseinstellungen.
        </p>
      </div>

      {/* Logo */}
      <SectionCard title="Firmenlogo">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{
            width: 80, height: 80, borderRadius: 12, border: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: T.bg, flexShrink: 0, overflow: 'hidden',
          }}>
            {logoUrl
              ? <img src={logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              : <span style={{ fontSize: 11, color: T.muted, textAlign: 'center', padding: 8 }}>Kein Logo</span>
            }
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-secondary"
              style={{ opacity: uploading ? 0.6 : 1 }}
            >
              {uploading ? 'Lädt hoch…' : 'Logo hochladen'}
            </button>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>PNG, JPG, SVG · Wird auf PDFs verwendet</div>
          </div>
        </div>
      </SectionCard>

      {/* Personal */}
      <SectionCard title="Persönliche Daten">
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
          <Field label="Vorname"  value={form.first_name} onChange={set('first_name')} placeholder="Max" />
          <Field label="Nachname" value={form.last_name}  onChange={set('last_name')}  placeholder="Mustermann" />
        </div>
        <Field label="E-Mail" value={user?.email || ''} onChange={() => {}} disabled />
        <Field label="Telefon" value={form.phone} onChange={set('phone')} placeholder="+49 89 12345678" type="tel" />
      </SectionCard>

      {/* Company */}
      <SectionCard title="Firmendaten">
        <Field label="Firmenname" value={form.company_name} onChange={set('company_name')} placeholder="Mustermann Immobilien GmbH" />
        <Field label="Adresse"    value={form.address}      onChange={set('address')}      placeholder="Musterstraße 1, 80333 München" />
        <Field label="USt-IdNr."  value={form.vat_id}       onChange={set('vat_id')}       placeholder="DE123456789" />
      </SectionCard>

      <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ marginBottom: 32, opacity: saving ? 0.6 : 1 }}>
        {saving ? 'Speichert…' : 'Änderungen speichern'}
      </button>

      {/* Password */}
      <SectionCard title="Passwort ändern">
        <form onSubmit={handlePasswordChange}>
          <Field label="Neues Passwort"       value={pwForm.next}     onChange={v => setPwForm(f => ({ ...f, next: v }))}    placeholder="min. 8 Zeichen" type="password" />
          <Field label="Passwort bestätigen"  value={pwForm.confirm}  onChange={v => setPwForm(f => ({ ...f, confirm: v }))} placeholder="Passwort wiederholen" type="password" />
          <button type="submit" disabled={pwSaving} className="btn-secondary" style={{ opacity: pwSaving ? 0.6 : 1 }}>
            {pwSaving ? 'Ändert…' : 'Passwort ändern'}
          </button>
        </form>
      </SectionCard>

      {/* Danger zone */}
      <div style={{ border: '1px solid #fecaca', background: '#fef2f2', borderRadius: 14, padding: '24px 28px', marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: '#dc2626', marginBottom: 12 }}>
          Konto löschen
        </div>
        <p style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.6, marginBottom: 16, marginTop: 0 }}>
          Alle deine Daten werden unwiderruflich gelöscht. Gib <strong>LÖSCHEN</strong> ein, um zu bestätigen.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={deleteConfirm}
            onChange={e => setDeleteConfirm(e.target.value)}
            placeholder="LÖSCHEN"
            style={{ border: '1px solid #fecaca', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: T.ink, outline: 'none', width: 160 }}
          />
          <button
            onClick={handleDeleteAccount}
            disabled={deleteConfirm !== 'LÖSCHEN'}
            style={{
              background: deleteConfirm === 'LÖSCHEN' ? '#dc2626' : '#e5e5e5',
              color: 'white', border: 'none', borderRadius: 8,
              padding: '10px 20px', fontSize: 13, fontWeight: 600,
              cursor: deleteConfirm === 'LÖSCHEN' ? 'pointer' : 'default',
            }}
          >
            Konto löschen
          </button>
        </div>
      </div>
    </div>
  );
}
